import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import BaseDialog from '@/components/ui/BaseDialog';
import BaseButton from '@/components/ui/BaseButton';
import { useTranslation } from 'react-i18next';
import noPreviewImage from '@/assets/images/no-preview.png';
import { useAppContext } from '@/provider/contexts';

interface PushImageDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (payload: { imgDataUrl: string }) => void;
    selectedCount: number;
    isPushing?: boolean;
    showProfileWarning?: boolean;
}

const PushImageDialog: React.FC<PushImageDialogProps> = ({
    open,
    onClose,
    onConfirm,
    selectedCount,
    isPushing = false,
    showProfileWarning = false,
}) => {
    const { t } = useTranslation('common');
    const { setNotification } = useAppContext();
    // const [bpp, setBpp] = useState<1 | 2 | 3 | 4 | 5>(3);
    const [file, setFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [dataUrl, setDataUrl] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // File picker open
    const openFilePicker = () => fileInputRef.current?.click();

    // Reset on close
    const resetState = () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setFile(null);
        setPreviewUrl(null);
        setDataUrl(null);
        // setBpp(3);
    };

    useEffect(() => {
        if (!open) resetState();
    }, [open]);

    const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;

        const allowed = ['image/bmp', 'image/jpeg', 'image/gif'];
        const validExt = /\.(bmp|jpe?g|gif)$/i.test(f.name);

        if (!allowed.includes(f.type) && !validExt) {
            setNotification({
                severity: 'error',
                message: t('Unsupported file type. Please use BMP, JPG/JPEG, or GIF.'),
            });
            e.target.value = '';
            return;
        }

        if (f.size > 8 * 1024 * 1024) {
            setNotification({
                severity: 'warning',
                message: t('File is large (>8MB). Consider resizing for better upload speed.'),
            });
        }

        const preview = URL.createObjectURL(f);
        setFile(f);
        setPreviewUrl(preview);

        const reader = new FileReader();
        reader.onload = () => setDataUrl(String(reader.result || ''));
        reader.readAsDataURL(f);
    };
    const handleConfirm = () => {
        if (!dataUrl) {
            setNotification({
                severity: 'warning',
                message: t('Please choose an image first.'),
            });
            return;
        }
        onConfirm({ imgDataUrl: dataUrl });
    };

    return (
        <BaseDialog
            open={open}
            dialogTitle={t('Push Image to Tag(s)')}
            confirmButtonText={t('Push')}
            cancelButtonText={t('Cancel')}
            onConfirm={handleConfirm}
            onClose={onClose}
            disableConfirm={isPushing || !dataUrl || selectedCount === 0}
            showSpinnerOnDisable={false}
        >
            <Box sx={{ display: 'grid', gap: 2, width: 560, maxWidth: '100%' }}>
                {/* File picker */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".bmp,.jpg,.jpeg,.gif"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                    <BaseButton variant="outlined" onClick={openFilePicker}>
                        {file ? t('Change File') : t('Choose File')}
                    </BaseButton>
                    <Typography variant="body2" color="text.secondary">
                        {file ? file.name : t('No file chosen')}
                    </Typography>
                </Box>

                {showProfileWarning && (
                    <Box
                        sx={{
                            mt: 1,
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: 'warning.light',
                            color: 'warning.dark',
                            border: (theme) => `1px solid ${theme.palette.warning.main}`,
                            fontSize: '0.875rem',
                            lineHeight: 1.4,
                        }}
                    >
                        <Typography variant="body2" sx={{ m: 0 }}>
                            {t(
                                'When pushing an image to different tag profiles, the image width and height will not be modified to match each different profile. In order to get the best result as possible, we suggest you send the image on a single tag profile at a time.',
                            )}
                        </Typography>
                    </Box>
                )}

                {/* Preview */}
                <Box>
                    <Typography sx={{ fontWeight: 600 }}>{t('Image Preview')}</Typography>

                    <Box
                        sx={{
                            borderRadius: 1,
                            border: '1px dashed',
                            borderColor: 'divider',
                            bgcolor: 'background.default',
                            display: 'inline-block',
                            maxWidth: '100%',
                            maxHeight: '60vh',
                            overflow: 'auto',
                            lineHeight: 0,
                            verticalAlign: 'top',
                        }}
                    >
                        {previewUrl ? (
                            <img
                                src={previewUrl}
                                alt="preview"
                                onError={() => {
                                    setNotification({
                                        severity: 'error',
                                        message: t('Failed to load the selected image. Please try another file.'),
                                    });
                                    setPreviewUrl(null);
                                    setDataUrl(null);
                                }}
                                style={{
                                    display: 'block',
                                    width: 'auto',
                                    height: 'auto',
                                    maxWidth: '100%',
                                    maxHeight: '60vh',
                                }}
                            />
                        ) : (
                            <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                <Box
                                    component="img"
                                    src={noPreviewImage}
                                    alt="no preview"
                                    sx={{
                                        width: 96,
                                        height: 96,
                                        opacity: 0.5,
                                        filter: 'grayscale(100%)',
                                        userSelect: 'none',
                                        pointerEvents: 'none',
                                    }}
                                />
                                {/* <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                                    {t('No image selected')}
                                </Typography> */}
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* BPP selection */}
                {/* <Box>
                    <Typography sx={{ mb: 0.5, fontWeight: 600 }}>{t('Color Mode (bpp)')}</Typography>
                    <FormControl>
                        <RadioGroup row value={bpp} onChange={(_, v) => setBpp(Number(v) as 1 | 2 | 3 | 4 | 5)}>
                            <FormControlLabel value={1} control={<Radio />} label="BW" />
                            <FormControlLabel value={2} control={<Radio />} label="BWR" />
                            <FormControlLabel value={3} control={<Radio />} label="BWRY" />
                            <FormControlLabel value={4} control={<Radio />} label="BWRY (Orange)" />
                            <FormControlLabel value={5} control={<Radio />} label="BWRY (Blue/Green)" />
                        </RadioGroup>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary">
                        {t("Supports BMP/PNG/JPG. Match your tag's color mode and keep image ≤ 8MB.")}
                    </Typography>
                </Box> */}

                {/* Selection info */}
                <Typography variant="caption" color="text.secondary">
                    {selectedCount > 0
                        ? t('Selected tags: {{count}}', { count: selectedCount })
                        : t('Select at least one "tag" device in the table.')}
                </Typography>
            </Box>
        </BaseDialog>
    );
};

export default PushImageDialog;
